import { CoachingExplanation }
  from "@/components/coaching/CoachingExplanation";
import { CoachingMetric, CoachingSectionCard }
  from "@/components/coaching/CoachingSectionCard";
import {
  formatComplianceCategory,
  formatConfidence,
  formatDate,
  formatMinutes,
  formatObjective,
  formatPercent,
  formatWeekType,
} from "@/lib/coachingFormatters";

const ComplianceRow = ({ label, value, testId }) => (
  <div className="flex justify-between text-xs py-1.5 border-b border-[var(--border-primary)]/50 last:border-b-0">
    <span className="card-label normal-case tracking-wide">{label}</span>
    <span data-testid={testId} className="text-dark-text-primary">{formatComplianceCategory(value)}</span>
  </div>
);

export const CurrentWeekCard = ({ week, compliance, timezone,
                                                complianceExplanation,
                                                error }) => {
  if (!week && !error) {
    return (
      <CoachingSectionCard
        title="This week"
        headingId="coaching-week-heading"
        testId="coaching-week-card"
        empty="Week information not available yet."
      />
    );
  }
  return (
    <CoachingSectionCard
      title={`Week ${week?.week_number ?? "—"}`}
      subtitle={formatWeekType(week?.week_type)}
      headingId="coaching-week-heading"
      testId="coaching-week-card"
      error={error}
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <CoachingMetric label="Starts" value={formatDate(week?.week_start_date, timezone)} testId="week-start" />
        <CoachingMetric label="Ends" value={formatDate(week?.week_end_date, timezone)} testId="week-end" />
        <CoachingMetric label="Objective" value={formatObjective(week?.primary_objective)} testId="week-objective" />
        <CoachingMetric label="Hard sessions" value={week?.hard_session_count ?? "—"} testId="week-hard-count" />
      </div>

      <div
        className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
        role="group"
        aria-label="Weekly volume: planned, completed of planned, and unplanned minutes"
      >
        <CoachingMetric label="Planned" value={formatMinutes(week?.target_total_minutes)} testId="week-planned-minutes" />
        <CoachingMetric label="Completed of planned" value={formatMinutes(week?.completed_planned_minutes)} testId="week-completed-minutes" />
        <CoachingMetric label="Unplanned" value={formatMinutes(week?.unplanned_minutes)} testId="week-unplanned-minutes" />
      </div>

      {compliance && (
        <div className="mt-5 app-card-elevated p-4">
          <div className="card-label mb-2">Compliance dimensions</div>
          <div className="space-y-0.5">
            <ComplianceRow label="Session completion" value={compliance.session_count_compliance} testId="compliance-sessions" />
            <ComplianceRow label="Duration" value={compliance.duration_compliance} testId="compliance-duration" />
            <ComplianceRow label="Objective" value={compliance.objective_compliance} testId="compliance-objective" />
            <ComplianceRow label="Key sessions" value={compliance.key_session_compliance} testId="compliance-key-sessions" />
            <ComplianceRow label="Intensity" value={compliance.intensity_compliance} testId="compliance-intensity" />
            <ComplianceRow label="Recovery" value={compliance.recovery_compliance} testId="compliance-recovery" />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 card-label">
            <span data-testid="compliance-weekly-score">
              Weekly score: {formatPercent(compliance.weekly_compliance_score)}
            </span>
            <span data-testid="compliance-confidence">
              {formatConfidence(compliance.confidence)}
            </span>
          </div>
        </div>
      )}

      {complianceExplanation && (
        <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
          <CoachingExplanation
            explanation={complianceExplanation}
            showMeta={false}
            testId="week-compliance-explanation"
          />
        </div>
      )}
    </CoachingSectionCard>
  );
};
